// A história de UM exame — abre do toque no exame da folha de laboratório.
//
// Pedido do Ilton (27/09): "só esse marcador" na tela, com a internação inteira
// num gráfico de subidas e descidas, um resumo curto e o que pode estar mexendo
// no valor — para o médico que clicar ver mais do que os últimos dias.
// Só na tela: o PDF continua a folha de uma página.
import { useEffect, useState } from 'react';
import { RODAPE_EXPLICACAO, formatarData, formatarDiaMes, formatarHora } from '@medicov1/shared';import { chamar } from '../api';
import { geometriaDoHistorico } from './graficoDoHistorico';

type Situacao = 'alto' | 'baixo' | 'dentro' | 'desconhecido';

export interface HistoricoDoMarcadorDTO {
  chave: string;
  rotulo: string;
  unidade: string;
  referencia: { min: number; max: number } | null;
  explicacao: { oQueE: string; porQueImporta: string | null; agora: string | null } | null;
  serie: { valor: number; texto: string; em: string; situacao: Situacao }[];
  resumo: { ultimo: { texto: string; em: string; situacao: Situacao }; direcao: 'subiu' | 'caiu' | 'estavel' | null; coletas: number } | null;
  frases: string[];
  influencias: {
    remedio: string; efeito: string; fonte: string | null; desde: string; ate: string | null; emUso: boolean;
    modo: 'horario' | 'se_necessario' | 'continuo' | null; doses: string[];
  }[];
  correlacoes: { id: string; titulo: string; texto: string; alerta: boolean; literatura: string; fontes: Fonte[] }[];
  imagem: { id: string; titulo: string; literatura: string; fontes: Fonte[]; laudos: { exame: string; data: string; presente: boolean; trecho: string }[] }[];
  base: { versao: number; revisadaEm: string; semCorrelacaoCatalogada: boolean; remediosSemRevisao: string[] };
}

interface Fonte { titulo: string; url?: string }

type Influencia = HistoricoDoMarcadorDTO['influencias'][number];

/**
 * Quando o remédio age. "Se necessário" não é "em uso desde": só age quando é
 * dado, e o médico precisa ver QUANDO foi dado (Ilton, 27/09).
 */
function quandoAge(i: Influencia): string {
  const periodo = i.emUso ? `prescrito desde ${formatarDiaMes(i.desde)}` : `de ${formatarDiaMes(i.desde)} a ${formatarDiaMes(i.ate!)}`;
  if (i.modo === 'se_necessario') {
    if (i.doses.length === 0) return `se necessário · ${periodo} · nenhuma dose registrada`;
    const ultimas = i.doses.slice(0, 3).map((d) => `${formatarDiaMes(new Date(d))} ${formatarHora(d)}`).join(', ');
    return `se necessário · ${i.doses.length} ${i.doses.length === 1 ? 'dose' : 'doses'}: ${ultimas}${i.doses.length > 3 ? '…' : ''}`;
  }
  if (!i.emUso) return periodo;
  return `em uso desde ${formatarDiaMes(i.desde)}${i.modo === 'continuo' ? ' (contínuo)' : ''}`;
}

function Fontes({ literatura, fontes }: { literatura: string; fontes: Fonte[] }) {
  return (
    <details>
      <summary>o que diz a literatura</summary>
      <p>{literatura}</p>
      <ul>{fontes.map((f) => <li key={f.titulo}>{f.url ? <a href={f.url} target="_blank" rel="noreferrer">{f.titulo}</a> : f.titulo}</li>)}</ul>
    </details>
  );
}

const L = 340;
const A = 176;
const SETA = { subiu: '↑', caiu: '↓', estavel: '→' } as const;

function Grafico({ h }: { h: HistoricoDoMarcadorDTO }) {
  // Só os remédios que começaram dentro do período medido viram linha no gráfico.
  // "Se necessário" não tem "começou": a linha marca a última dose dada (ou nenhuma).
  const marcas = h.influencias.map((i) => ({
    em: i.modo === 'se_necessario' ? (i.doses[0] ?? '') : `${i.desde}T12:00:00-03:00`,
  }));
  const g = geometriaDoHistorico(h.serie, h.referencia, marcas, L, A);
  if (g.pontos.length === 0) return null;
  return (
    <svg className="hm__grafico" viewBox={`0 0 ${L} ${A}`} role="img"
      aria-label={`${h.rotulo} na internação: ${h.serie.map((p) => p.texto).join(', ')}`}>
      <rect x={g.area.x0} y={g.area.y0} width={g.area.x1 - g.area.x0} height={g.area.y1 - g.area.y0} className="hm__area" />
      {g.faixa && <rect x={g.area.x0} y={g.faixa.y} width={g.area.x1 - g.area.x0} height={g.faixa.h} className="hm__faixa" />}
      {g.yTicks.map((t) => (
        <text key={`y${t.valor}`} x={g.area.x0 - 5} y={t.y + 3.5} className="hm__eixo" textAnchor="end">{t.valor.toLocaleString('pt-BR')}</text>
      ))}
      {g.xTicks.map((t, i) => (
        <text key={`x${i}`} x={t.x} y={A - 6} className="hm__eixo" textAnchor={i === 0 ? 'start' : i === g.xTicks.length - 1 ? 'end' : 'middle'}>{formatarDiaMes(new Date(t.em))}</text>
      ))}
      {g.marcas.map((x, i) => x === null ? null : (
        <g key={`m${i}`}>
          <line x1={x} x2={x} y1={g.area.y0} y2={g.area.y1} className="hm__marca" />
          <text x={x} y={g.area.y0 - 3} className="hm__marca-n" textAnchor="middle">{i + 1}</text>
        </g>
      ))}
      {g.pontos.length > 1 && <polyline points={g.pontos.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')} className="hm__linha" />}
      {g.pontos.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={i === g.pontos.length - 1 ? 4 : 2.4} className={`hm__ponto hm__ponto--${h.serie[i]!.situacao}`} />
      ))}
    </svg>
  );
}

interface Props { pacienteId: string; chave: string; aoFechar: () => void }

export function HistoricoDoMarcador({ pacienteId, chave, aoFechar }: Props) {
  const [h, setH] = useState<HistoricoDoMarcadorDTO | null>(null);
  const [erro, setErro] = useState('');
  const [todas, setTodas] = useState(false);

  useEffect(() => {
    chamar<HistoricoDoMarcadorDTO>(`/exames/historico?pacienteId=${pacienteId}&marcador=${encodeURIComponent(chave)}`)
      .then(setH)
      .catch((e) => setErro(e instanceof Error ? e.message : 'não consegui buscar a história deste exame'));
  }, [pacienteId, chave]);

  // Esc fecha; e a página de trás não rola enquanto isto está aberto.
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') aoFechar(); };
    document.addEventListener('keydown', tecla);
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', tecla); document.body.style.overflow = antes; };
  }, [aoFechar]);

  const medidas = h ? [...h.serie].reverse() : [];
  const visiveis = todas ? medidas : medidas.slice(0, 12);

  return (
    <div className="hm" role="dialog" aria-modal="true" aria-label={h ? `História: ${h.rotulo}` : 'História do exame'}>
      <article className="doc hm__folha">
        <header className="hm__topo">
          <div>
            <span className="hm__rotulo-pequeno">História na internação</span>
            <h2>{h?.rotulo ?? '…'}</h2>
            {h?.referencia && <small>faixa {h.referencia.min.toLocaleString('pt-BR')}–{h.referencia.max.toLocaleString('pt-BR')} {h.unidade}</small>}
          </div>
          <button className="botao botao--voltar" onClick={aoFechar}>Fechar</button>
        </header>

        {erro && <p className="aviso-erro">{erro}</p>}
        {!h && !erro && <p className="fraco">carregando…</p>}

        {h && (
          <>
            {h.resumo && (
              <div className={`hm__ultimo hm__ultimo--${h.resumo.ultimo.situacao}`}>
                <b>{h.resumo.ultimo.texto}{h.resumo.direcao && <span> {SETA[h.resumo.direcao]}</span>}</b>
                <small>{h.unidade} · {formatarData(new Date(h.resumo.ultimo.em))} · {h.resumo.coletas} {h.resumo.coletas === 1 ? 'medida' : 'medidas'}</small>
              </div>
            )}

            <Grafico h={h} />
            <p className="hm__legenda">
              Faixa verde: o normal.{h.influencias.length > 0 && ' Linhas numeradas: quando começou cada remédio da lista abaixo (no "se necessário", a última dose dada).'}
            </p>

            <ul className="hm__frases">{h.frases.map((f) => <li key={f}>{f}</li>)}</ul>

            {h.explicacao && (
              <section className="hm__bloco">
                <h3>O que é</h3>
                <p>{h.explicacao.oQueE}{h.explicacao.porQueImporta ? ` ${h.explicacao.porQueImporta}` : ''}</p>
                {h.explicacao.agora && <p className="hm__agora"><b>{h.resumo?.ultimo.situacao === 'alto' ? 'Acima da faixa' : 'Abaixo da faixa'}, em geral:</b> {h.explicacao.agora}</p>}
              </section>
            )}

            {h.influencias.length > 0 && (
              <section className="hm__bloco">
                <h3>O que pode mexer neste valor</h3>
                <ol className="hm__influencias">
                  {h.influencias.map((i) => (
                    <li key={`${i.remedio}|${i.desde}`}>
                      <b>{i.remedio}</b>
                      <small>{quandoAge(i)}</small>
                      {!i.emUso && <span className="hm__fora">fora de uso</span>}
                      <span>{i.efeito}</span>
                      {i.fonte && <span className="hm__fonte">fonte: {i.fonte}</span>}
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {h.correlacoes.length > 0 && (
              <section className="hm__bloco">
                <h3>Junto com outros exames</h3>
                <ul className="hm__correlacoes">
                  {h.correlacoes.map((c) => (
                    <li key={c.id} className={`hm__correlacao${c.alerta ? ' hm__correlacao--alerta' : ''}`}>
                      <b>{c.titulo}</b>
                      <p>{c.texto}</p>
                      <Fontes literatura={c.literatura} fontes={c.fontes} />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {h.imagem.length > 0 && (
              <section className="hm__bloco">
                <h3>Nos laudos de imagem</h3>
                <ul className="hm__correlacoes">
                  {h.imagem.map((a) => (
                    <li key={a.id} className="hm__correlacao">
                      <b>{a.titulo}</b>
                      <ul className="hm__laudos">
                        {a.laudos.map((l) => (
                          <li key={`${l.exame}|${l.data}`}>
                            <small>{formatarDiaMes(l.data)}</small>{l.exame}: <em className={l.presente ? 'hm__sim' : 'hm__nao'}>{l.presente ? 'presente' : 'ausente'}</em>
                            <q>{l.trecho}</q>
                          </li>
                        ))}
                      </ul>
                      <Fontes literatura={a.literatura} fontes={a.fontes} />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {(h.base.semCorrelacaoCatalogada || h.base.remediosSemRevisao.length > 0) && (
              <p className="hm__agora">
                {h.base.semCorrelacaoCatalogada && 'Nenhuma correlação catalogada para este exame na base — revisar manualmente. '}
                {h.base.remediosSemRevisao.length > 0 && `Remédios do período ainda não revisados na base: ${h.base.remediosSemRevisao.join(', ')}.`}
              </p>
            )}

            {medidas.length > 0 && (
              <section className="hm__bloco">
                <h3>Todas as medidas</h3>
                <table className="hm__tabela">
                  <tbody>
                    {visiveis.map((p, i) => (
                      <tr key={p.em + i}>
                        <td>{formatarData(new Date(p.em))}{h.chave === 'glicemia_capilar' && <small> {formatarHora(p.em)}</small>}</td>
                        <td className={`hm__valor hm__valor--${p.situacao}`}>{p.texto}</td>
                        <td className="fraco">{p.situacao === 'alto' ? 'acima' : p.situacao === 'baixo' ? 'abaixo' : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {medidas.length > 12 && (
                  <button className="hm__mais" onClick={() => setTodas((t) => !t)}>
                    {todas ? 'mostrar só as 12 mais recentes' : `ver todas as ${medidas.length}`}
                  </button>
                )}
              </section>
            )}

            <p className="hm__rodape fraco">
              {RODAPE_EXPLICACAO} "Pode mexer" é efeito conhecido do remédio, não a causa do valor dela. As correlações vêm de uma base
              de literatura revisada em {formatarData(h.base.revisadaEm)} (versão {h.base.versao}), com a fonte de cada regra.
            </p>
          </>
        )}
      </article>
    </div>
  );
}
